<template>
  <section class="admin-content" id="add-ticket">
    <Navbar v-if="!isSelfService" />
    <main class="admin-main" :class="{ 'public-main': isSelfService }">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0;">
            <div class="header-icon-wrap mr-3">
              <i class="fe fe-life-buoy" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">Create Ticket</h4>
              <small style="opacity: 0.8;">Log an enrollee support case and assign the right team</small>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <form @submit.prevent="submitTicket">
            <div class="card shadow-sm form-card mb-4">
              <div class="form-card-header">
                <div class="section-number">1</div>
                <h6 class="mb-0 font-weight-bold text-white">Enrollee Lookup</h6>
              </div>

              <div class="card-body px-4 pt-3">
                <div class="form-row">
                  <div class="form-group col-md-8">
                    <label class="small font-weight-bold text-secondary">Search Enrollee <span
                        class="text-danger">*</span></label>
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <span class="input-group-text bg-white"><i class="fe fe-search text-muted"></i></span>
                      </div>
                      <input type="text" class="form-control border-left-0"
                        placeholder="Enrollee ID, Phone number, NIN ..." v-model="searchkey"
                        @keyup.enter="searchIDCard" />
                      <div class="input-group-append">
                        <button type="button" class="btn btn-info" @click="searchIDCard"
                          :disabled="isLoading || !searchkey">
                          <i class="fe fe-search mr-1"></i> Search
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="form-group col-md-4">
                    <label class="small font-weight-bold text-secondary">Select Enrollee <span
                        class="text-danger">*</span></label>
                    <select class="form-control" v-model="ticket.user_id" required>
                      <option value="" disabled>Select a matched enrollee...</option>
                      <option v-for="result in searchResults" :key="result.id" :value="result.id">
                        {{ enrolleeLabel(result) }}
                      </option>
                    </select>
                    <small class="text-muted">{{ searchResults.length }} result(s)</small>
                  </div>

                  <div class="form-group col-md-6">
                    <label class="small font-weight-bold text-secondary">Enrollee Email Address
                      <span v-if="emailRequired" class="text-danger">*</span></label>
                    <input type="email" class="form-control" v-model="ticket.email" :required="emailRequired"
                      placeholder="enrollee@example.com" />
                    <small class="text-muted" v-if="emailRequired">
                      No email on file for this enrollee — please enter one.
                    </small>
                    <small class="text-muted" v-else>Used to send updates as this ticket is treated.</small>
                  </div>
                </div>

                <div v-if="selectedEnrollee" class="p-3 rounded mt-2 summary-card">
                  <div class="d-flex align-items-center">
                    <div class="summary-avatar mr-3">
                      <i class="fe fe-user"></i>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-1 font-weight-bold">{{ enrolleeLabel(selectedEnrollee) }}</h6>
                      <div class="d-flex flex-wrap summary-meta">
                        <small class="text-muted">ID Card: {{ selectedEnrollee.id_card_number || 'N/A' }}</small>
                        <small class="text-muted">Phone: {{ selectedEnrollee.phone_number || 'N/A' }}</small>
                        <small class="text-muted">Sector: {{ selectedEnrollee.sector || selectedEnrollee.type || 'N/A'
                          }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="card shadow-sm form-card mb-4">
              <div class="form-card-header">
                <div class="section-number">2</div>
                <h6 class="mb-0 font-weight-bold text-white">Ticket Details</h6>
              </div>

              <div class="card-body px-4 pt-3">
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label class="small font-weight-bold text-secondary">Ticket Type <span
                        class="text-danger">*</span></label>
                    <select class="form-control" v-model="ticket.type" required>
                      <option value="" disabled>Select type...</option>
                      <option value="Inquiry">Inquiry</option>
                      <option value="Request">Request</option>
                      <option value="Complaint">Complaint</option>
                      <option value="Grieviance">Grieviance</option>
                      <option value="Quality of Service">Quality of Service</option>
                      <option value="Administrative">Administrative</option>
                      <option value="Financial Issue">Financial Issue</option>
                      <option value="Out of State Treatment">Out of State Treatment</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>

                  <div class="form-group col-md-6">
                    <label class="small font-weight-bold text-secondary">Status</label>
                    <select class="form-control" v-model="ticket.status">
                      <option value="open">Open</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </div>

                  <div class="form-group col-md-6">
                    <label class="small font-weight-bold text-secondary">Source <span
                        class="text-danger">*</span></label>
                    <select class="form-control" v-model="ticket.source" required>
                      <option value="" disabled>Select source...</option>
                      <option value="Call Centre">Call Centre</option>
                      <option value="Website">Website</option>
                      <option value="WhatsApp">WhatsApp</option>
                      <option value="Facebook">Facebook</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Instagram">Instagram</option>
                      <option value="X">X</option>
                      <option value="Office">Office</option>
                    </select>
                  </div>

                  <div class="form-group col-md-6">
                    <label class="small font-weight-bold text-secondary">Priority <span
                        class="text-danger">*</span></label>
                    <select class="form-control" v-model="ticket.priority" required>
                      <option value="" disabled>Select priority...</option>
                      <option value="Urgent">Urgent</option>
                      <option value="Important">Important</option>
                      <option value="Routine">Routine</option>
                    </select>
                    <span v-if="ticket.priority"
                      :class="`priority-chip priority-chip--${ticket.priority.toLowerCase()} mt-1 d-inline-block`">
                      {{ ticket.priority }}
                    </span>
                  </div>

                  <div class="form-group col-md-12">
                    <label class="small font-weight-bold text-secondary">Title <span
                        class="text-danger">*</span></label>
                    <input type="text" class="form-control" v-model="ticket.title" placeholder="Summarize the issue"
                      required />
                  </div>

                  <div class="form-group col-md-12">
                    <label class="small font-weight-bold text-secondary">Description <span
                        class="text-danger">*</span></label>
                    <textarea class="form-control" rows="5" v-model="ticket.description"
                      placeholder="Describe the issue, expected resolution, and any urgency" required></textarea>
                  </div>
                </div>
              </div>
            </div>

            <div class="card shadow-sm form-card mb-4" v-if="canAssignTicket && !isSelfService">
              <div class="form-card-header">
                <div class="section-number">3</div>
                <h6 class="mb-0 font-weight-bold text-white">Assignment</h6>
              </div>

              <div class="card-body px-4 pt-3">
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label class="small font-weight-bold text-secondary">Provider</label>
                    <select class="form-control" v-model="ticket.provider_id">
                      <option :value="null">Select provider...</option>
                      <option v-for="provider in providers" :key="provider.id" :value="provider.id">
                        {{ providerLabel(provider) }}
                      </option>
                    </select>
                  </div>

                  <div class="form-group col-md-6">
                    <label class="small font-weight-bold text-secondary">Assign Staff</label>
                    <v-select v-model="ticket.staff_ids" :options="staffOptions"
                      :get-option-label="s => `${s.full_name} (${s.job_title})`" :reduce="s => s.id" multiple
                      placeholder="Select staff members…" />
                    <small class="text-muted">Type to search. Click × to remove.</small>
                  </div>
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-end">
              <button type="submit" class="btn btn-submit px-4" :disabled="isLoading || !ticket.user_id">
                <i class="fe fe-send mr-2"></i>
                {{ isLoading ? 'Submitting...' : 'Submit Ticket' }}
              </button>
            </div>
          </form>
        </div>

        <div class="vld-parent">
          <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
  import Navbar from "@/views/Navbar.vue";
  import Loading from "vue-loading-overlay";
  import "vue-loading-overlay/dist/vue-loading.css";

  export default {
    components: { Navbar, Loading },
    data() {
      return {
        agency: JSON.parse(localStorage.getItem("agency")),
        user: JSON.parse(localStorage.getItem("user")),
        isLoading: false,
        fullPage: true,
        searchkey: "",
        searchResults: [],
        providers: [],
        staffOptions: [],
        ticket: {
          user_id: "",
          title: "",
          description: "",
          type: "",
          provider_id: null,
          staff_ids: [],
          status: "open",
          email: "",
          source: "",
          priority: "urgent",
        },
      };
    },
    computed: {
      selectedEnrollee() {
        return this.searchResults.find((item) => item.id === this.ticket.user_id) || null;
      },
      currentAgencyId() {
        if (this.selectedEnrollee && this.selectedEnrollee.agency_id) {
          return this.selectedEnrollee.agency_id;
        }
        return this.$route.params.agency_id || (this.agency && this.agency.id) || null;
      },
      canAssignTicket() {
        return this.user && this.user.type !== "client";
      },
      isSelfService() {
        return this.$route.name === "raise-ticket";
      },
      emailRequired() {
        return !this.selectedEnrollee || !this.selectedEnrollee.email;
      },
    },
    watch: {
      selectedEnrollee(enrollee) {
        this.ticket.email = (enrollee && enrollee.email) || "";
      },
    },
    methods: {
      enrolleeLabel(item) {
        if (!item) {
          return "";
        }

        return item.user_query || item.full_name || `${item.firstname || ""} ${item.lastname || ""}`.trim();
      },
      providerLabel(provider) {
        return provider.agency_name || provider.provider_name || provider.full_name || provider.firstname || `Provider #${provider.id}`;
      },
      normalizeCollection(payload) {
        if (Array.isArray(payload)) {
          return payload;
        }

        if (payload && Array.isArray(payload.data)) {
          return payload.data;
        }

        return [];
      },
      searchIDCard() {
        if (!this.searchkey) {
          return;
        }

        this.isLoading = true;
        this.axios
          .post(`/api/v1/auth/searchenrollees`, {
            agency_id: this.currentAgencyId,
            request_query: this.searchkey,
          })
          .then((response) => {
            this.searchResults = this.normalizeCollection(response.data);
            this.ticket.user_id = this.searchResults.length ? this.searchResults[0].id : "";
            this.isLoading = false;
          })
          .catch((error) => {
            console.error(error);
            this.searchResults = [];
            this.ticket.user_id = "";
            this.isLoading = false;
            this.$toasted.info("User not found", { position: "top-center", duration: 3000 });
          });
      },
      fetchProviders() {
        const agencyId = (this.user && this.user.institutional_id) || this.currentAgencyId;
        if (!agencyId) return;
        this.axios
          .get(`/api/v1/auth/providerAgency/${agencyId}`)
          .then((response) => {
            this.providers = this.normalizeCollection(response.data);
          })
          .catch((error) => {
            console.error(error);
          });
      },
      fetchAssignableStaff() {
        if (!this.currentAgencyId) return;
        this.axios
          .get(`/api/v1/auth/getEmployee/${this.currentAgencyId}`)
          .then((response) => {
            this.staffOptions = this.normalizeCollection(response.data);
          })
          .catch((error) => {
            console.error(error);
          });
      },
      submitTicket() {
        if (!this.ticket.user_id) {
          this.$toasted.info("Select an enrollee before submitting", { position: "top-center", duration: 3000 });
          return;
        }

        this.isLoading = true;
        this.axios
          .post(`/api/v1/auth/makeComplaints`, {
            user_id: this.ticket.user_id,
            agency_id: this.currentAgencyId,
            title: this.ticket.title,
            description: this.ticket.description,
            type: this.ticket.type,
            provider_id: this.ticket.provider_id,
            staff_ids: this.ticket.staff_ids,
            status: this.ticket.status,
            email: this.ticket.email,
            source: this.ticket.source,
            priority: this.ticket.priority,
          })
          .then(() => {
            this.isLoading = false;
            this.$toasted.success("Ticket created successfully! We'll be in touch shortly.", { position: "top-center", duration: 4000 });
            this.$router.push(this.isSelfService ? "/self-service-auth" : "/my-complaints");
          })
          .catch((error) => {
            console.error(error.response || error);
            this.isLoading = false;
          });
      },
    },
    created() {
      if (this.user && this.user.type === "client") {
        this.searchResults = [this.user];
        this.ticket.user_id = this.user.id;
      }
      if (this.canAssignTicket) {
        this.fetchProviders();
        this.fetchAssignableStaff();
      }
    },
  };
</script>

<style scoped>
  .priority-chip {
    padding: 3px 12px;
    border-radius: 20px;
    font-size: .75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .03em;
  }

  .priority-chip--urgent {
    background: #fee2e2;
    color: #991b1b;
  }

  .priority-chip--important {
    background: #fef3c7;
    color: #92400e;
  }

  .priority-chip--routine {
    background: #ede0d4;
    color: #6b4423;
  }

  /* No Navbar means no reserved sidebar/header space is needed. */
  .public-main {
    padding-top: 20px !important;
    padding-left: 0 !important;
  }

  .page-header {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    padding-bottom: 40px;
  }

  .header-icon-wrap {
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .form-card {
    border-radius: 12px;
    border: 1px solid #e9ecef;
    overflow: hidden;
  }

  .form-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 20px;
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  }

  .section-number {
    width: 28px;
    height: 28px;
    background: rgba(255, 255, 255, 0.25);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 700;
    font-size: 0.8rem;
    flex-shrink: 0;
  }

  .summary-card {
    background: #e8f7f9;
    border: 1px solid #b2e0e8;
  }

  .summary-avatar {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: #dbeafe;
    color: #2563eb;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
  }

  .summary-meta {
    gap: 16px;
  }

  .btn-submit {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    border: none;
    color: #fff;
    border-radius: 8px;
    font-weight: 600;
    padding: 10px 24px;
  }

  .btn-submit:hover {
    opacity: 0.9;
    color: #fff;
  }

  .form-control:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 0.15rem rgba(37, 99, 235, 0.15);
  }
</style>